import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
// Constants - Functions
import { WEB_SERVICE_MIMS, errorColor } from "../../../../../../config/constants";
// Components
import {
  MainDiv,
  Header,
  AssessmentTitle,
  OpenButton,
  OpenButtonImg,
  Body,
  ProtocolsInfo,
  DatesAligner,
  DatesDiv,
  TitleDiv,
  DateDiv,
  DateText,
  StatusDiv,
  StatusText,
  StatusImg,
  OpenPopupButton,
  OpenPopupImg,
  PopupDiv,
  PopupTitle,
  DatePopupDiv,
  InputDateDiv,
  InputDate,
  ActionsDiv,
  CancelButton,
  SaveButton,
} from "./styles";
import Popup from "../../../../../popup";
import UseFetch from "../../../../../../config/useFetch";
import Loader from "../../../../../loader";
// Assets
import ArrowUpImg from "../../../../../../assets/icons/ico_arrow_up_blue.svg";
import ArrowDownImg from "../../../../../../assets/icons/ico_arrow_down_blue.svg";
import CheckIco from "../../../../../../assets/icons/check_white.svg";
import WarningIco from "../../../../../../assets/icons/ico_warning_white.svg";
import HourglassIco from "../../../../../../assets/icons/hourglass.svg";
import EditBlueIco from "../../../../../../assets/icons/btn_edit_user_blue.svg";
import EditGrayIco from "../../../../../../assets/icons/btn_edit_user_gray.svg";

const IntrinsicMeasuresAssessments = ({
  patientData,
  intrinsicDates,
  isLoadingIntrinsicDates,
  newIntrinsicDate,
  setNewIntrinsicDate,
  newMeasureKey,
  setNewMeasureKey,
  showIntrinsicDatePopup,
  setShowIntrinsicDatePopup,
  refreshIntrinsicDates,
  setRefreshIntrinsicDates,
}) => {
  const [newIntrinsicDateError, setNewIntrinsicDateError] = useState(false);
  const [showAssessmentDates, setShowAssessmentDates] = useState(false);
  const { patientId } = useParams();

  // Show intrinsic dates component if there is at least one incomplete status
  useEffect(() => {
    const incompleteIntrinsicDates = intrinsicDates.filter(
      (intrinsicDate) => intrinsicDate.STATUS === "IS_NOT_COMPLETED_ORANGE" || intrinsicDate.STATUS === "IS_NOT_PRESCRIBED"
    );
    if (incompleteIntrinsicDates.length) setShowAssessmentDates(true);

    return () => {
      setShowAssessmentDates(false);
    };
  }, [intrinsicDates]);

  const renderIntrinsicDates = () => {
    return intrinsicDates.map((intrinsicDate) => {
      const getStatusInfo = () => {
        if (intrinsicDate.STATUS === "IS_COMPLETED") {
          return {
            background: "#22B573",
            img: CheckIco,
            text: "COMPLETED",
          };
        }
        if (intrinsicDate.STATUS === "IS_NOT_COMPLETED_GRAY") {
          return {
            background: "#C8C8C8",
            img: WarningIco,
            text: "NOT COMPLETED",
          };
        }
        if (intrinsicDate.STATUS === "IS_NOT_COMPLETED_ORANGE") {
          return {
            background: "#F15A24",
            img: WarningIco,
            text: "NOT COMPLETED",
          };
        }
        if (intrinsicDate.STATUS === "IS_PRESCRIBED") {
          return {
            background: "#FDCD56",
            img: HourglassIco,
            text: "PRESCRIBED",
          };
        }
        if (intrinsicDate.STATUS === "IS_NOT_PRESCRIBED") {
          return {
            background: "#F15A24",
            img: WarningIco,
            text: "NOT PRESCRIBED",
          };
        }
        return {
          background: "#C8C8C8",
          img: HourglassIco,
          text: "WAITING",
        };
      };
      const { background, img, text } = getStatusInfo();
      const isNotCompleted = intrinsicDate.STATUS === "IS_NOT_COMPLETED_ORANGE" || intrinsicDate.STATUS === "IS_NOT_PRESCRIBED";
      const isClickable = intrinsicDate.STATUS !== "IS_COMPLETED" && intrinsicDate.STATUS !== "IS_NOT_COMPLETED_GRAY";

      const handleSetPopupData = () => {
        setNewIntrinsicDate(intrinsicDate.DATE ? new Date(moment(intrinsicDate.DATE, "DD/MM/YYYY")) : "");
        setNewMeasureKey(intrinsicDate.KEY);
        setShowIntrinsicDatePopup(true);
      };

      return (
        <DatesAligner key={intrinsicDate.KEY}>
          <DatesDiv
            border={isNotCompleted ? "1.5px solid #F15A24" : "none"}
            background={isNotCompleted ? "rgba(241, 90, 36, 0.10)" : "#f3f3f3"}
          >
            <TitleDiv>
              <AssessmentTitle>{intrinsicDate.KEY}</AssessmentTitle>
            </TitleDiv>
            <DateDiv>
              <DateText>{intrinsicDate.DATE ? intrinsicDate.DATE : "-"}</DateText>
            </DateDiv>
            <StatusDiv background={background}>
              <StatusText>{text}</StatusText>
              <StatusImg src={img} alt="status-ico" />
            </StatusDiv>
          </DatesDiv>
          <OpenPopupButton onClick={handleSetPopupData} clickable={isClickable}>
            <OpenPopupImg src={isClickable ? EditBlueIco : EditGrayIco} alt="edit-ico" />
          </OpenPopupButton>
        </DatesAligner>
      );
    });
  };

  const renderSetIntrinsicDatePopup = () => {
    const patientCreationDate = moment(patientData.CREATION_TIME).format("DD/MM/YYYY");
    const routineStartingDate = patientData.aisnData.ROUTINE_STARTING_DATE;
    const routineEndingDate = moment(routineStartingDate, "DD/MM/YYYY").add(12, "weeks").format("DD/MM/YYYY");

    const getInitialDate = () => {
      if (newMeasureKey === "Baseline") return patientCreationDate;
      if (newMeasureKey === "End of therapy") return routineEndingDate;
      return moment(routineStartingDate, "DD/MM/YYYY").add(20, "weeks").format("DD/MM/YYYY");
    };
    const getFinalDate = () => {
      if (newMeasureKey === "Baseline") return routineStartingDate;
      if (newMeasureKey === "End of therapy") return moment(routineStartingDate, "DD/MM/YYYY").add(20, "weeks").format("DD/MM/YYYY");
      return "01/01/2100";
    };

    const validDates = (date) => {
      const initialDate = moment(getInitialDate(), "DD/MM/YYYY");
      const finalDate = moment(getFinalDate(), "DD/MM/YYYY");

      return (
        moment(date).isSameOrAfter(moment(), "day") &&
        moment(date).isSameOrAfter(initialDate, "day") &&
        moment(date).isBefore(finalDate, "day")
      );
    };

    const firstAvailableDate = () => {
      if (newIntrinsicDate) return newIntrinsicDate;
      const initialDate = moment(getInitialDate(), "DD/MM/YYYY");
      if (initialDate.isBefore(moment(), "day")) return new Date();
      return initialDate.toDate();
    };

    const updateIntrinsicDate = () => {
      if (!newIntrinsicDate) {
        setNewIntrinsicDateError(true);
        return toast.error("You must select a date");
      }

      UseFetch("post", `${WEB_SERVICE_MIMS}/recsys/update-intrinsic-date/${patientId}`, new AbortController(), {
        measureKey: newMeasureKey,
        newIntrinsicDate: moment(newIntrinsicDate).format("YYYY/MM/DD"),
      })
        .then(({ data }) => {
          if (data.intrinsicAndPrescriptionsUpdated) {
            toast.success("Date saved successfully");
            setShowIntrinsicDatePopup(false);
            setRefreshIntrinsicDates(!refreshIntrinsicDates);
          }
        })
        .catch((error) => {
          console.log("Save Intrinsic data: ", error);
        });
    };

    const handleSetDate = (date) => {
      setNewIntrinsicDate(date);
      setNewIntrinsicDateError(false);
    };

    return (
      <Popup isShowed={showIntrinsicDatePopup} setIsShowed={setShowIntrinsicDatePopup}>
        <PopupDiv>
          <PopupTitle>Set date</PopupTitle>
          <DatePopupDiv>
            <AssessmentTitle>{newMeasureKey}</AssessmentTitle>
            <InputDateDiv>
              <InputDate
                selected={newIntrinsicDate}
                onChange={(date) => handleSetDate(date)}
                filterDate={validDates}
                openToDate={firstAvailableDate()}
                placeholderText="dd/mm/yyyy"
                dateFormat={"dd/MM/yyyy"}
                border={newIntrinsicDateError ? `1px solid ${errorColor}` : `1px solid #C8C8C8`}
              />
            </InputDateDiv>
          </DatePopupDiv>
          <ActionsDiv>
            <CancelButton onClick={() => setShowIntrinsicDatePopup(false)}>Cancel</CancelButton>
            <SaveButton onClick={updateIntrinsicDate}>Save</SaveButton>
          </ActionsDiv>
        </PopupDiv>
      </Popup>
    );
  };

  return (
    <>
      {isLoadingIntrinsicDates ? (
        <Loader height={50} />
      ) : (
        <>
          <MainDiv>
            <Header>
              <AssessmentTitle>RGS Diagnostics at the Clinic</AssessmentTitle>
              <OpenButton onClick={() => setShowAssessmentDates(!showAssessmentDates)}>
                <OpenButtonImg src={showAssessmentDates ? ArrowUpImg : ArrowDownImg} alt="arrow-ico" />
              </OpenButton>
            </Header>
            {showAssessmentDates && (
              <Body>
                <ProtocolsInfo>
                  Diagnostic activities to be performed at the clinic: RGS+ (Quality Control, Constellations, Vertical AR Circle, and
                  Horizontal AR Circle) + RGSclinic (Pinball)
                </ProtocolsInfo>
                {renderIntrinsicDates()}
              </Body>
            )}
          </MainDiv>
          {showIntrinsicDatePopup && renderSetIntrinsicDatePopup()}
        </>
      )}
    </>
  );
};

export default IntrinsicMeasuresAssessments;
